import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import TopNavigation from './TopNavigation';
import BottomNavigation from './BottomNavigation';
import SearchBar from './SearchBar';
import CategoryTabs from './CategoryTabs';
import BiohackCard from './BiohackCard';
import BiohackInfoCard from './BiohackInfoCard';
import ProtocolBuilder from './ProtocolBuilder';
import ShieldsPickerModal from './ShieldsPickerModal';
import ShareProtocolModal from './ShareProtocolModal';
import Onboarding from './Onboarding';
import { useOnboarding } from '../hooks/useOnboarding';
import { Icon } from './Icon.js';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import './CreateProtocol.css';

const CreateProtocol = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [biohacks, setBiohacks] = useState([]);
  const [filteredBiohacks, setFilteredBiohacks] = useState([]);
  const [selectedHacks, setSelectedHacks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showDropSuccess, setShowDropSuccess] = useState(false);
  const [showDropRemove, setShowDropRemove] = useState(false);
  const [dropRemoveMessage, setDropRemoveMessage] = useState('Biohack removed!');
  const [shouldClearInputs, setShouldClearInputs] = useState(false);
  const [biohackToShow, setBiohackToShow] = useState(null);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState('');
  const [hasRatedBiohack, setHasRatedBiohack] = useState(false);
  const [ratedBiohackIds, setRatedBiohackIds] = useState(new Set()); // Set of biohack IDs the user has rated
  const [showShieldsPicker, setShowShieldsPicker] = useState(false);
  const [showShareProtocolModal, setShowShareProtocolModal] = useState(false);
  const [newlyCreatedProtocol, setNewlyCreatedProtocol] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasConsented, setHasConsented] = useState(null); // null = not checked yet, true/false = checked
  const [pendingProtocolData, setPendingProtocolData] = useState(null); // Store protocol data while waiting for consent
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  // Use centralized onboarding hook
  const [showOnboarding, setShowOnboarding] = useOnboarding({
    shouldCheck: !!userId,
    userId: userId
  });

  // Fetch biohacks and categories on component mount
  useEffect(() => {
    fetchBiohacks();
    fetchCategories();
    fetchFavourites();
    fetchUserId();
  }, []);

  // Fetch user ratings for all biohacks once userId is available
  useEffect(() => {
    if (userId && biohacks.length > 0) {
      fetchUserRatings();
    }
  }, [userId, biohacks.length]);

  // Handle biohack from search
  useEffect(() => {
    if (location.state?.openBiohackId && biohacks.length > 0) {
      const biohackToOpen = biohacks.find(b => b.id === location.state.openBiohackId);
      if (biohackToOpen) {
        setBiohackToShow(biohackToOpen);
        // Check if user has rated this biohack
        checkIfRated(biohackToOpen.id);
        // Clear the state so it doesn't reopen on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, biohacks, userId]);

  // Check if user came from onboarding - no longer needed, main onboarding handles it
  // Removed separate biohack onboarding modal

  // Check if onboarding should be shown (for main onboarding flow)

  const checkIfRated = async (biohackId) => {
    if (!userId || !biohackId) return;
    try {
      const { data, error } = await supabase
        .from('biohack_ratings')
        .select('id')
        .eq('user_id', userId)
        .eq('hack_id', biohackId)
        .single();
      
      setHasRatedBiohack(!!data);
    } catch (error) {
      setHasRatedBiohack(false);
    }
  };

  const fetchUserId = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (user) {
        setUserId(user.id);
        // Fetch username from user_profiles
        fetchUsername(user.id);
      } else {
        setUserId(null);
        setUsername('');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchUsername = async (userId) => {
    try {
      // Check cache first
      const cachedProfile = sessionStorage.getItem(`userProfile_${userId}`);
      if (cachedProfile) {
        try {
          const parsedProfile = JSON.parse(cachedProfile);
          if (parsedProfile.username) {
            setUsername(parsedProfile.username);
            return;
          }
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      // Fetch from database
      const { data, error } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data?.username) {
        setUsername(data.username);
      }
    } catch (error) {
      console.error('Error fetching username:', error);
    }
  };

  // Check if user has health data consent
  const checkHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const { data, error } = await supabase
        .from('health_data_consent')
        .select('id')
        .eq('user_id', userId)
        .eq('consent_type', 'health_biometric_processing')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking health data consent:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking health data consent:', error);
      return false;
    }
  };

  // Store health data consent
  const storeHealthDataConsent = async (userId) => {
    if (!userId) return false;
    try {
      const consentText = "I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.";
      const consentTextVersion = "v1.0-2026-01-12";

      const { error } = await supabase
        .from('health_data_consent')
        .insert({
          user_id: userId,
          consent_type: 'health_biometric_processing',
          consent_text_version: consentTextVersion,
          consent_text_snapshot: consentText,
          given_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error storing health data consent:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing health data consent:', error);
      return false;
    }
  };

  // Check if user has accepted the protocol creation disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'protocol_card_creation')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'protocol_card_creation')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'protocol_card_creation',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Filter biohacks based on search and category
  useEffect(() => {
    let filtered = biohacks;

    // Filter by category
    if (activeCategory === 'favourites') {
      filtered = biohacks.filter(hack => 
        favourites.some(fav => fav.hack_id === hack.id)
      );
    } else if (activeCategory !== 'all') {
      filtered = biohacks.filter(hack => hack.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(hack =>
        hack.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hack.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBiohacks(filtered);
  }, [biohacks, activeCategory, searchQuery, favourites]);

  // Reset shouldClearInputs flag after it's been used
  useEffect(() => {
    if (shouldClearInputs) {
      // Reset the flag after a short delay to allow the ProtocolBuilder to process it
      const timer = setTimeout(() => {
        setShouldClearInputs(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldClearInputs]);

  const fetchBiohacks = async () => {
    try {
      // Use deduplication to prevent duplicate requests (especially from React StrictMode)
      const cacheKey = getCacheKey('CreateProtocol_fetchBiohacks', 'all');
      const biohacksData = await dedupeRequest(cacheKey, async () => {
        const { data, error } = await supabase
          .from('biohacks')
          .select('*')
          .order('name');

        if (error) throw error;
        return data || [];
      });

      setBiohacks(biohacksData);
    } catch (error) {
      console.error('Error fetching biohacks:', error);
    } finally {
      setLoading(false);
      
      // Add a small delay to prevent flash of empty state
      setTimeout(() => {
        setDataReady(true);
      }, 100);
    }
  };

  const fetchCategories = async () => {
    try {
      // Use deduplication to prevent duplicate requests (especially from React StrictMode)
      const cacheKey = getCacheKey('CreateProtocol_fetchCategories', 'all');
      const categoriesData = await dedupeRequest(cacheKey, async () => {
        const { data, error } = await supabase
          .from('biohacks')
          .select('category')
          .not('category', 'is', null);

        if (error) throw error;
        return data || [];
      });
      
      const uniqueCategories = [...new Set(categoriesData.map(item => item.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFavourites = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Use deduplication to prevent duplicate requests (especially from React StrictMode)
      const cacheKey = getCacheKey('fetchFavourites', user.id);
      const favouritesData = await dedupeRequest(cacheKey, async () => {
        const { data, error } = await supabase
          .from('hack_favourited')
          .select('hack_id')
          .eq('user_id', user.id);

        if (error) throw error;
        return data || [];
      });

      setFavourites(favouritesData);
    } catch (error) {
      console.error('Error fetching favourites:', error);
    }
  };

  const fetchUserRatings = async () => {
    if (!userId) return;
    try {
      // Fetch all biohack IDs that the user has rated in a single query
      const { data, error } = await supabase
        .from('hack_scoring')
        .select('hack_id')
        .eq('user_id', userId);

      if (error) throw error;
      
      // Create a Set of rated biohack IDs for O(1) lookup
      const ratedIds = new Set((data || []).map(rating => rating.hack_id));
      setRatedBiohackIds(ratedIds);
    } catch (error) {
      console.error('Error fetching user ratings:', error);
      setRatedBiohackIds(new Set());
    }
  };

  const handleAddToProtocol = (hack) => {
    if (selectedHacks.length >= 8) {
      setErrorMessage('Maximum 8 biohacks allowed per protocol');
      setShowErrorMessage(true);
      
      // Hide error message after 3 seconds
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
      return;
    }
    
    if (selectedHacks.some(selected => selected.id === hack.id)) {
      setErrorMessage('This biohack is already in your protocol');
      setShowErrorMessage(true);
      
      // Hide error message after 3 seconds
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
      return;
    }

    setSelectedHacks([...selectedHacks, hack]);
  };

  const handleRemoveFromProtocol = (hackId) => {
    setSelectedHacks((prev) => {
      const updated = prev.filter(hack => hack.id !== hackId);
      if (updated.length !== prev.length) {
        setDropRemoveMessage('Biohack removed!');
        setShowDropRemove(true);
        setTimeout(() => setShowDropRemove(false), 1000);
      }
      return updated;
    });
  };

  const handleClearAllHacks = (count = selectedHacks.length) => {
    if (count === 0) return;
    setSelectedHacks([]);
    setDropRemoveMessage(count > 1 ? 'Biohacks removed!' : 'Biohack removed!');
    setShowDropRemove(true);
    setTimeout(() => setShowDropRemove(false), 1000);
  };

  const handleToggleFavourite = async (hackId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const isFavourited = favourites.some(fav => fav.hack_id === hackId);

      if (isFavourited) {
        // Remove from favourites
        const { error } = await supabase
          .from('hack_favourited')
          .delete()
          .eq('user_id', user.id)
          .eq('hack_id', hackId);

        if (error) throw error;
        setFavourites(prevFavourites => {
          const newFavourites = prevFavourites.filter(fav => fav.hack_id !== hackId);
          return newFavourites;
        });
      } else {
        // Add to favourites
        const { error } = await supabase
          .from('hack_favourited')
          .insert({
            user_id: user.id,
            hack_id: hackId,
            timestamp: new Date().toISOString()
          });

        if (error) throw error;
        setFavourites(prevFavourites => {
          const newFavourites = [...prevFavourites, { hack_id: hackId }];
          return newFavourites;
        });
      }
    } catch (error) {
      console.error('Error toggling favourite:', error);
    }
  };

  const handleProtocolSave = async (protocolData) => {
    // Prevent duplicate calls
    if (isSaving) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setErrorMessage('Please sign in to save protocols');
        setShowErrorMessage(true);
        
        // Hide error message after 3 seconds
        setTimeout(() => {
          setShowErrorMessage(false);
          setErrorMessage('');
        }, 3000);
        return;
      }

      // Check if user has accepted the disclaimer
      const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(user.id);
      
      if (!hasAcceptedDisclaimer) {
        // Fetch disclaimer text and show modal
        const disclaimer = await fetchDisclaimerText();
        if (disclaimer && disclaimer.text) {
          setDisclaimerText(disclaimer.text);
          setDisclaimerVersion(disclaimer.version || 'v1.0');
          setPendingProtocolData(protocolData);
          setShowDisclaimerModal(true);
          setAcceptResponsibility(false);
          setDoNotShowAgain(false);
          return;
        }
      }

      // Check if user has health data consent
      const userHasConsented = await checkHealthDataConsent(user.id);
      
      if (!userHasConsented) {
        // Store protocol data as pending and show consent modal
        setPendingProtocolData(protocolData);
        setShowConsentModal(true);
        return;
      }

      // User has accepted disclaimer and consented, proceed with saving
      await saveProtocolData(protocolData, user);
    } catch (error) {
      console.error('Error in handleProtocolSave:', error);
      setErrorMessage('Error creating protocol. Please try again.');
      setShowErrorMessage(true);
      
      // Hide error message after 3 seconds
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
    }
  };

  // Handle consent modal actions
  const handleConsent = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        setShowConsentModal(false);
        return;
      }

      // Store consent in database
      const consentStored = await storeHealthDataConsent(user.id);
      
      if (consentStored) {
        setHasConsented(true);
        setShowConsentModal(false);
        
        // If there's pending protocol data, save it now
        if (pendingProtocolData) {
          await saveProtocolData(pendingProtocolData, user);
          setPendingProtocolData(null);
        }
      } else {
        setErrorMessage('Error storing consent. Please try again.');
        setShowErrorMessage(true);
        setTimeout(() => {
          setShowErrorMessage(false);
          setErrorMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error handling consent:', error);
      setErrorMessage('Error storing consent. Please try again.');
      setShowErrorMessage(true);
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
    }
  };

  const handleNoConsent = () => {
    // Just close the modal, nothing changes
    setShowConsentModal(false);
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        setShowDisclaimerModal(false);
        return;
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(user.id, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // Continue with protocol creation flow
        // Check health data consent next
        const userHasConsented = await checkHealthDataConsent(user.id);
        
        if (!userHasConsented && pendingProtocolData) {
          // Store protocol data as pending and show consent modal
          setShowConsentModal(true);
        } else if (pendingProtocolData) {
          // User has accepted disclaimer and consented, proceed with saving
          await saveProtocolData(pendingProtocolData, user);
          setPendingProtocolData(null);
        }
      } else {
        setErrorMessage('Error storing disclaimer acceptance. Please try again.');
        setShowErrorMessage(true);
        setTimeout(() => {
          setShowErrorMessage(false);
          setErrorMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      setErrorMessage('Error storing disclaimer acceptance. Please try again.');
      setShowErrorMessage(true);
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
    setPendingProtocolData(null);
  };

  const saveProtocolData = async (protocolData, user) => {
    setIsSaving(true);
    try {
      // Check if user already has a protocol with this name
      const { data: existingUserProtocols } = await supabase
        .from('protocols_status')
        .select(`
          protocols!inner(id, name)
        `)
        .eq('user_id', user.id)
        .eq('protocols.name', protocolData.name);

      if (existingUserProtocols && existingUserProtocols.length > 0) {
        setErrorMessage('You already have a protocol with this name');
        setShowErrorMessage(true);
        
        // Hide error message after 3 seconds
        setTimeout(() => {
          setShowErrorMessage(false);
          setErrorMessage('');
        }, 3000);
        setIsSaving(false);
        return;
      }

      // Insert protocol (shared template)
      const { data: protocol, error: protocolError } = await supabase
        .from('protocols')
        .insert({
          created_by: user.id,
          name: protocolData.name,
          description: protocolData.description
        })
        .select()
        .single();

      if (protocolError) {
        console.error('Protocol creation error:', protocolError);
        throw protocolError;
      }

      // Create protocol status record for the creator
      const { data: protocolStatus, error: statusError } = await supabase
        .from('protocols_status')
        .insert({
          user_id: user.id,
          protocol_id: protocol.id,
          is_active: protocolData.status === 'active',
          added_at: new Date().toISOString(),
          activated_at: protocolData.status === 'active' ? new Date().toISOString() : null
        })
        .select()
        .single();

      if (statusError) {
        console.error('Protocol status creation error:', statusError);
        throw statusError;
      }

      // Insert protocol hacks
      if (selectedHacks.length === 0) {
        // No biohacks to add
      } else {
        const protocolHacks = selectedHacks.map((hack, index) => ({
          protocol_id: protocol.id,
          hack_id: hack.id,
          position: index
        }));

        const { error: hacksError } = await supabase
          .from('protocol_hacks')
          .insert(protocolHacks);

        if (hacksError) {
          console.error('Protocol hacks creation error:', hacksError);
          throw hacksError;
        }
      }

      // Insert logging schedules if schedule option is 'normal' and there are confirmed schedules
      if (protocolData.scheduleOption === 'normal' && protocolData.scheduleData && protocolData.confirmedSchedules && protocolData.confirmedSchedules.length > 0) {
        const scheduleEntries = [];
        
        // Only save schedules that were confirmed (user pressed Done or Apply to all)
        protocolData.confirmedSchedules.forEach(hackId => {
          const schedule = protocolData.scheduleData[hackId];
          
          // Verify schedule is complete before saving
          if (schedule && 
              schedule.interval !== null && 
              schedule.interval !== undefined && 
              schedule.intervalUnit && 
              schedule.times && 
              schedule.times.length > 0) {
            
            // Create an entry for each time (in case multiple times are set)
            schedule.times.forEach(timeEntry => {
              scheduleEntries.push({
                user_id: user.id,
                protocol_id: protocol.id,
                hack_id: hackId,
                time: timeEntry,
                unit: schedule.intervalUnit, // 'days', 'weeks', 'months'
                number: schedule.interval // e.g., 2 for "every 2 days"
              });
            });
          }
        });

        // Insert schedule entries if any
        if (scheduleEntries.length > 0) {
          const { error: scheduleError } = await supabase
            .from('logging_schedules')
            .insert(scheduleEntries);

          if (scheduleError) {
            console.error('Logging schedules creation error:', scheduleError);
            throw scheduleError;
          }
        }
      }

      // Fetch the full protocol data with biohacks and creator to show in modal
      const { data: fullProtocol, error: fetchError } = await supabase
        .from('protocols')
        .select(`
          *,
          user_profiles!protocols_created_by_fkey (
            username
          ),
          protocol_hacks (
            hack_id,
            position,
            biohacks (
              id,
              name,
              image_url
            )
          )
        `)
        .eq('id', protocol.id)
        .single();

      if (fetchError) {
        console.error('Error fetching full protocol:', fetchError);
      }

      // Show success message with styled indicator
      setSuccessMessage(protocolData.status === 'active' ? 'Protocol created and activated!' : 'Protocol created!');
      setShowSuccessMessage(true);
      
      // Store the newly created protocol and show the share modal
      if (fullProtocol) {
        // Format protocol to match expected structure (keep both protocol_hacks and biohacks for compatibility)
        const formattedProtocol = {
          ...fullProtocol,
          biohacks: (fullProtocol.protocol_hacks || [])
            .sort((a, b) => a.position - b.position)
            .map(h => h.biohacks)
            .filter(Boolean),
          // Keep protocol_hacks structure for compatibility with getBiohacksFromProtocol
          protocol_hacks: fullProtocol.protocol_hacks || [],
          // Ensure creator username is available for ProtocolShield (creator is current user)
          created_by_username: fullProtocol.user_profiles?.username || username
        };
        setNewlyCreatedProtocol(formattedProtocol);
        setShowShareProtocolModal(true);
      }
      
      // Clear selected biohacks to reset the protocol builder
      setSelectedHacks([]);
      
      // Clear protocol input fields
      setShouldClearInputs(true);
      
      // Hide success message after 1 second (same as biohack added)
      setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 1000);
      
      // navigate('/protocols'); // Remove this line to stay on the same page
    } catch (error) {
      console.error('Error creating protocol:', error);
      setErrorMessage('Error creating protocol. Please try again.');
      setShowErrorMessage(true);
      
      // Hide error message after 3 seconds
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage('');
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="create-protocol">
      <TopNavigation activePage="create-protocol" />
      
      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-filter-container">
          <SearchBar 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search biohacks..."
          />
          <CategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
          {loading && (
            <div className="subtle-loading-indicator">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
          )}
        </div>
      </div>



      {/* Biohacks Grid */}
      <div className="biohacks-grid-container">
        <div className="biohacks-grid">
          {loading || biohacks.length === 0 || !dataReady ? (
            // Show skeleton loading cards while data is loading OR when no biohacks exist yet
            // OR when data isn't ready yet (prevents flash)
            Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="biohack-skeleton">
                <div className="skeleton-image"></div>
                <div className="skeleton-title"></div>
              </div>
            ))
          ) : (
            // Only show content when we have stable data
            filteredBiohacks.length > 0 ? (
              filteredBiohacks.map((hack) => (
                <BiohackCard
                  key={hack.id}
                  hack={hack}
                  isFavourited={favourites.some(fav => fav.hack_id === hack.id)}
                  isSelected={selectedHacks.some(selected => selected.id === hack.id)}
                  hasRated={ratedBiohackIds.has(hack.id)}
                  onAddToProtocol={handleAddToProtocol}
                  onToggleFavourite={handleToggleFavourite}
                  onRatingSubmitted={() => {
                    // Update the rated biohack IDs when a new rating is submitted
                    setRatedBiohackIds(prev => new Set([...prev, hack.id]));
                  }}
                />
              ))
            ) : (
              // Show simple empty state when a search/filter yields no results
              <p className="biohacks-empty-message">No biohacks found</p>
            )
          )}
        </div>
      </div>

      {/* Protocol Builder Bar */}
      <ProtocolBuilder
        selectedHacks={selectedHacks}
        onRemoveHack={handleRemoveFromProtocol}
        onAddHack={handleAddToProtocol}
        onClearAll={handleClearAllHacks}
        onSaveProtocol={handleProtocolSave}
        onNavigateToMyProtocols={() => navigate('/user#protocols')}
        shouldClearInputs={shouldClearInputs}
        isSaving={isSaving}
        currentUsername={username}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorMessage(true);
          
          // Hide error message after 3 seconds
          setTimeout(() => {
            setShowErrorMessage(false);
            setErrorMessage('');
          }, 3000);
        }}
        onShowDropSuccess={() => {
          setShowDropSuccess(true);
          setTimeout(() => setShowDropSuccess(false), 1000);
        }}
        onShowDropRemove={() => {
          setDropRemoveMessage('Biohack removed!');
          setShowDropRemove(true);
          setTimeout(() => setShowDropRemove(false), 1000);
        }}
      />

      {/* Success Message Indicator */}
      {showSuccessMessage && (
        <div className="protocol-success-indicator">
          <Icon name="Check" className="icon--sm" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Message Indicator */}
      {showErrorMessage && (
        <div className="protocol-error-indicator">
          <Icon name="AlertTriangle" className="icon--sm" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Drop Success Indicator */}
      {showDropSuccess && (
        <div className="drop-success-indicator">
          <Icon name="Check" className="icon--sm" />
          <span>Biohack added!</span>
        </div>
      )}

      {/* Drop Remove Indicator */}
      {showDropRemove && (
        <div className="drop-remove-indicator">
          <Icon name="X" className="icon--sm" />
          <span>{dropRemoveMessage}</span>
        </div>
      )}

      {/* BiohackInfoCard - opened from search */}
      {biohackToShow && (
        <BiohackInfoCard
          biohack={biohackToShow}
          onClose={() => setBiohackToShow(null)}
          onAddToProtocol={handleAddToProtocol}
          isFavourited={favourites.some(fav => fav.hack_id === biohackToShow.id)}
          onToggleFavourite={() => handleToggleFavourite(biohackToShow.id)}
          hasRated={hasRatedBiohack}
          onRatingSubmitted={() => {
            setHasRatedBiohack(true);
            // Also update the rated biohack IDs set
            if (biohackToShow) {
              setRatedBiohackIds(prev => new Set([...prev, biohackToShow.id]));
            }
          }}
        />
      )}

      {/* Floating "Try Me!" Button */}
      <button 
        className="floating-try-me-button"
        onClick={() => setShowShieldsPicker(true)}
        title="Browse and add pre-made protocol shields"
      >
        <Icon name="Sparkles" className="icon--md" />
        <span>Try Me!</span>
      </button>

      {/* Shields Picker Modal */}
      {showShieldsPicker && (
        <ShieldsPickerModal
          onClose={() => setShowShieldsPicker(false)}
          onPopulateBuilder={(biohacks) => {
            // Populate the builder bar with the protocol's biohacks
            // Filter out any biohacks that are already selected and respect the 8 biohack limit
            const newHacks = biohacks.filter(hack => 
              !selectedHacks.some(selected => selected.id === hack.id)
            ).slice(0, 8 - selectedHacks.length);
            
            if (newHacks.length === 0) {
              setErrorMessage('All biohacks from this protocol are already in your builder');
              setShowErrorMessage(true);
              setTimeout(() => {
                setShowErrorMessage(false);
                setErrorMessage('');
              }, 3000);
              return;
            }
            
            if (newHacks.length < biohacks.length) {
              setErrorMessage(`Added ${newHacks.length} biohack${newHacks.length > 1 ? 's' : ''} (some were already in builder or limit reached)`);
              setShowErrorMessage(true);
              setTimeout(() => {
                setShowErrorMessage(false);
                setErrorMessage('');
              }, 3000);
            }
            
            setSelectedHacks([...selectedHacks, ...newHacks]);
            setSuccessMessage(`Added ${newHacks.length} biohack${newHacks.length > 1 ? 's' : ''} to builder!`);
            setShowSuccessMessage(true);
            setTimeout(() => {
              setShowSuccessMessage(false);
              setSuccessMessage('');
            }, 2000);
          }}
        />
      )}

      {/* Protocol Creation Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Protocol Creation Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for my protocol</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Data Consent Modal */}
      {showConsentModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Health Data Consent</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You are unable to create a protocol without clicking the I consent button.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can withdraw this consent at any time in account settings.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleNoConsent}
                  style={{ flex: 1 }}
                >
                  I don't consent
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConsent}
                  style={{ flex: 1 }}
                >
                  I consent
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Protocol Modal */}
      {showShareProtocolModal && newlyCreatedProtocol && (
        <ShareProtocolModal
          protocol={newlyCreatedProtocol}
          onClose={() => {
            setShowShareProtocolModal(false);
            setNewlyCreatedProtocol(null);
          }}
          onPostCreated={() => {
            // Optional: refresh data or show success message
          }}
        />
      )}
      <BottomNavigation />
      {showOnboarding && (
        <Onboarding onComplete={() => setShowOnboarding(false)} />
      )}
    </div>
  );
};

export default CreateProtocol; 